import {Children, HTMLAttributes, JSX, ReactElement, ReactNode, Ref, isValidElement, useCallback, useEffect, useRef} from "react";

type SlotProps = HTMLAttributes<HTMLDivElement>;
type SlotDivProps = Omit<SlotProps, "children">;

function mergeClasses(base: string, extra?: string) {
	return extra ? `${base} ${extra}` : base;
}

function Leading({children}: SlotProps): JSX.Element {
	return <>{children}</>;
}

function Trailing({children}: SlotProps): JSX.Element {
	return <>{children}</>;
}

function Content({children}: SlotProps): JSX.Element {
	return <>{children}</>;
}

export interface SwiperProps extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
	ref?: Ref<HTMLDivElement>;
}

function SwiperBase({children, className, ref, ...rest}: SwiperProps): JSX.Element {
	const internalRef = useRef<HTMLDivElement>(null);

	const mergedRef = useCallback((node: HTMLDivElement | null) => {
		internalRef.current = node;
		if (typeof ref === "function") ref(node);
		else if (ref) ref.current = node;
	}, [ref]);

	let leadingContent: ReactNode = null;
	let leadingProps: SlotDivProps = {};
	let trailingContent: ReactNode = null;
	let trailingProps: SlotDivProps = {};
	let mainContent: ReactNode = null;
	let mainProps: SlotDivProps = {};

	Children.forEach(children, (child) => {
		if (!isValidElement(child)) return;
		const element = child as ReactElement<SlotProps>;
		if (element.type === Leading) {
			const {children: c, ...r} = element.props;
			leadingContent = c;
			leadingProps = r;
		} else if (element.type === Trailing) {
			const {children: c, ...r} = element.props;
			trailingContent = c;
			trailingProps = r;
		} else if (element.type === Content) {
			const {children: c, ...r} = element.props;
			mainContent = c;
			mainProps = r;
		}
	});

	useEffect(() => {
		if (!internalRef.current) return;
		//esto es porque el hack usando animation en css no funciona en safari.
		const parent = internalRef.current;
		const hold = parent.querySelector(".swiper-content") as HTMLDivElement;

		requestAnimationFrame(() => {
			parent.scrollLeft = hold.offsetLeft;
		});
	}, []);


	return (
		<div {...rest} className={mergeClasses("swiper", className)} ref={mergedRef}>
			{leadingContent && <div {...leadingProps} className={mergeClasses("swiper-leading", leadingProps.className)}>{leadingContent}</div>}
			{trailingContent && <div {...trailingProps} className={mergeClasses("swiper-trailing", trailingProps.className)}>{trailingContent}</div>}
			<div {...mainProps} className={mergeClasses("swiper-content", mainProps.className)}>{mainContent}</div>
		</div>
	);
}

const Swiper: typeof SwiperBase & { Leading: typeof Leading; Trailing: typeof Trailing; Content: typeof Content; } = Object.assign(SwiperBase, {Leading, Trailing, Content});
export default Swiper;
